import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface IReportRange {
  startAt: number;
  endAt: number;
}

export const getTodayReportRange = (timezone: number): IReportRange => {
  const currentTime = dayjs().utcOffset(timezone * 60);

  return {
    startAt: currentTime.startOf('date').unix(),
    endAt: currentTime.unix(),
  };
};

/**
 * Report ranges are wall clock values in the timezone the user picked, not in
 * the browser timezone. Every place that reads or writes hour, date, or month
 * on a report timestamp has to go through this so the day boundary follows the
 * selected timezone.
 */
export const reportDayjs = (unixSeconds: number, timezone: number) =>
  dayjs(unixSeconds * 1000).utcOffset(timezone * 60);

/**
 * Keeps the same wall clock reading when the picked timezone changes, so
 * switching from GMT+7 to GMT+8 still shows the same calendar day.
 */
export const rebaseRangeToTimezone = (
  unixSeconds: number,
  fromTimezone: number,
  toTimezone: number
) => unixSeconds - (toTimezone - fromTimezone) * 3600;

/**
 * A range that ends today, no later than now, is a "today until now" range.
 * When the picked timezone changes it keeps ending at now, so the latest data
 * is still fetched. Any other end keeps its wall clock like the start does.
 */
export const rebaseRangeEndToTimezone = (
  endAt: number,
  fromTimezone: number,
  toTimezone: number,
  now: number = dayjs().unix()
) => {
  const endsTodayUntilNow =
    endAt <= now &&
    reportDayjs(endAt, fromTimezone).isSame(
      reportDayjs(now, fromTimezone),
      'day'
    );

  return endsTodayUntilNow
    ? now
    : rebaseRangeToTimezone(endAt, fromTimezone, toTimezone);
};
